import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import Items from './items/pages/Items';
import MainNavigation from './shared/components/Navigation/MainNavigation';
import TopBar from './shared/components/Navigation/TopBar';
import Auth from './auth/Auth';
import { AuthContext } from './shared/context/auth-context';
import Customers from './customer/pages/Customers';
import localforage from 'localforage';
import axios from 'axios';
import Inicio from './inicio/Inicio';
import Horas from './customer/pages/Horas';
import ReporteHoras from './horas/pages/ReporteHoras';
import { createAppTheme } from './theme';

// Import session test utilities for development
if (process.env.NODE_ENV === 'development') {
  import('./test-session-timeout.js');
}

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState('');
  const [mode, setMode] = useState('light');
  const [navCollapsed, setNavCollapsed] = useState(false);

  const getUserThemeKey = useCallback((u) => {
    const id = u?.email || u?.sub || u?.id;
    return id ? `themeMode_${id}` : 'themeMode';
  }, []);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem(getUserThemeKey(user), next);
      return next;
    });
  }, [user, getUserThemeKey]);

  // Apply user-specific theme preference whenever user is resolved
  useEffect(() => {
    if (user) {
      const saved = localStorage.getItem(getUserThemeKey(user));
      if (saved) setMode(saved);
    }
  }, [user, getUserThemeKey]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  axios.defaults.baseURL = process.env.REACT_APP_BACKEND_BASE_URL;

  useEffect(() => {
    (async function () {
      try {
        // Check for new session structure first
        const userSession = await localforage.getItem('userSession');

        if (userSession) {
          // Check if session has expired
          const currentTime = new Date().getTime();
          const sessionAge = currentTime - userSession.loginTime;

          if (sessionAge > userSession.expiresIn) {
            // Session expired - clean up and logout
            console.log('Session expired after 24 hours');
            await localforage.removeItem('userSession');
            await localforage.removeItem('user'); // Clean up old format too
            setIsLoggedIn(false);
            setUser(null);
            return;
          }

          // Session is valid
          setIsLoggedIn(true);
          setUser(userSession.user);
          return;
        }

        // Fallback: check for old user format and migrate
        const legacyUser = await localforage.getItem('user');
        if (legacyUser) {
          // Migrate to new session format
          const sessionData = {
            user: legacyUser,
            loginTime: new Date().getTime(),
            expiresIn: 24 * 60 * 60 * 1000 // 24 hours
          };

          await localforage.setItem('userSession', sessionData);
          await localforage.removeItem('user'); // Remove old format
          setIsLoggedIn(true);
          setUser(legacyUser);
        }
      } catch (err) {
        console.log('Error checking session:', err);
      }
    })();
  }, []);

  const login = useCallback((loggedInUser) => {
    setIsLoggedIn(true);
    if (loggedInUser) setUser(loggedInUser);
  }, []);
  const logout = useCallback(() => {
    localforage.removeItem('userSession', (err) => {
      if (!err) {
        setUser(null);
        setIsLoggedIn(false);
      } else {
        console.log(err);
      }
    });
  }, []);

  let routes;

  if (isLoggedIn) {
    routes = (
      <Fragment>
        <Route path="/" element={<Inicio />}></Route>
        <Route path="/clientes" element={<Customers />}></Route>
        <Route path="/horas" element={<Horas />}></Route>
        <Route path="/items" element={<Items />}></Route>
        <Route path="/reporte-horas" element={<ReporteHoras />}></Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Fragment>
    );
  } else {
    routes = (
      <Fragment>
        <Route path="/auth" element={<Auth />}></Route>
        <Route path="*" element={<Navigate to="/auth" replace />} />
      </Fragment>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <AuthContext.Provider value={{ isLoggedIn, login, logout }}>
          <Router>
            <Box sx={{ display: 'flex' }}>
              <MainNavigation
                user={user}
                mode={mode}
                onToggle={toggleMode}
                collapsed={navCollapsed}
                onToggleCollapsed={() => setNavCollapsed((prev) => !prev)}
              />
              <Box
                sx={{
                  flexGrow: 1,
                  mt: { xs: '56px', md: 0 },
                  minHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column'
                }}>
                {isLoggedIn && <TopBar mode={mode} onToggle={toggleMode} />}
                <Box
                  component="main"
                  sx={{
                    flexGrow: 1,
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    py: { xs: 1.5, md: 2 }
                  }}>
                  <Routes>{routes}</Routes>
                </Box>
              </Box>
            </Box>
          </Router>
        </AuthContext.Provider>
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
};

export default App;
